import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane, Button } from 'reactstrap';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import CreateTargetManuallyForm from './Form/CreateTargetManualyForm';
import CreateWithAIForm from '../../audience/AudienceCreate/CreateWithAIForm';
import { getAudienceProfileTypes } from '../../../../slices/veloxity/audience/AudienceProfileTypes';
import {  getAllTargetAudience  } from '../../../../slices/veloxity/targetAudience/getAllTargetAudience';
import { getProducts } from '../../../../slices/veloxity/audience/GetProductsReducer';
import { createTargetManualy } from '../../../../slices/veloxity/targetAudience/createTargetManualy';
import { createAudienceDefinePreview } from '../../../../slices/veloxity/audience/CreateAudienceDefinePreview';
import { createTargetDefineAudience } from '../../../../slices/veloxity/targetAudience/createTargetDefineAudience';
import LottieAnimation from '../../../../assets/animation/LottieAnimation';
import AudiencePreviewModal from '../../../../components/modal/audience/AudiencePreviewModal';
import { showSuccessAlert, showErrorAlert } from '../../../../components/custom/sweetalertHelper';

const CreateTargetAudience = ({ isOpen, toggle, updateLoading, onSuccess, onError }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [createAudienceName, setCreateAudienceName] = useState('');
  const [defineAudienceName, setDefineAudienceName] = useState('');
  const [defineAudienceText, setDefineAudienceText] = useState('');
  const [profileTypeOptions, setProfileTypeOptions] = useState({ ios: [], android: [] });
  const [selectedPersonaType, setSelectedPersonaType] = useState([]);
  const [existingNames, setExistingNames] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [popup, setPopup] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);  
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);


  const dispatch = useDispatch();
  const { status: operationStatus } = useSelector(state => state.createTargetManualySlice || {});
  const isLoadingDefine = useSelector(state => state.createTargetDefineAudience.loading);



  useEffect(() => {
    const fetchProfileTypes = async () => {
      try {
        const iosResponse = await dispatch(getAudienceProfileTypes('IOS')).unwrap();
        const androidResponse = await dispatch(getAudienceProfileTypes('ANDROID')).unwrap();

        const iosOptions = iosResponse.items.map(item => ({ value: item.code, label: item.name }));
        const androidOptions = androidResponse.items.map(item => ({ value: item.code, label: item.name }));

        setProfileTypeOptions({ ios: iosOptions, android: androidOptions });
      } catch (error) {
        console.error('Error fetching profile types:', error);
      }
    };

    fetchProfileTypes();
  }, [dispatch]);

  useEffect(() => {
    if (operationStatus === 'succeeded' || operationStatus === 'failed') {
      dispatch(getAllTargetAudience());
    }
  }, [operationStatus, dispatch]);

  useEffect(() => {
    const loadExistingNames = async () => {
      try {
        const audiences = await dispatch(findAllVeloxityAudience()).unwrap();
        const names = audiences.map(audience => audience.name);
        setExistingNames(names);
      } catch (error) {
        console.error('Error loading existing names:', error);
      }
    };

    loadExistingNames();
  }, [dispatch]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const checkNameExists = (name) => {
    const normalizedExistingNames = existingNames.map(name => name.toLowerCase());
    if (normalizedExistingNames.includes(name.toLowerCase())) {
      showErrorAlert("Error", "Name already exists");
      updateLoading(false);
      return true;
    }
    return false;
  };
  const handleConfirmDefine = (e) => {
    e.preventDefault();
    
    const nameInput = defineAudienceName;
    const osTypeInput = "ANDROID";
    const userStatusInput = "ACTIVE";
    
    if (checkNameExists(nameInput)) {
      return;
    }
    
    if (!previewData) {
      showErrorAlert("Error", "Preview data not found");
      return;
    }
    
    updateLoading(true);
    
    const audienceData = {
      name: nameInput,
      userStatus: previewData.userStatus || userStatusInput,
      definition: previewData.definition || defineAudienceText,
      explain: previewData.explain,
      audienceQuery: previewData.audienceQuery,
      osType: osTypeInput,
      target: true
    };
    
    dispatch(createTargetDefineAudience(audienceData))
      .then((action) => {
        if (action.type.endsWith('fulfilled')) {
          showSuccessAlert("Success", "Audience created successfully!");
          setShowPreviewModal(false);  
       

        } else {
          showErrorAlert("Error", "Failed to create audience.");
        }
      })
      .catch((error) => {
        console.error('An error occurred:', error);
        showErrorAlert("Error", error.message || "Failed to create audience.");
      })
      .finally(() => {
        toggle()
        updateLoading(false);
      });
  };
  
  

  const handleDefineSubmit = async (e) => {
    e.preventDefault();
    setPreviewLoading(true);

    const nameInput = defineAudienceName.trim();
    const definitionText = defineAudienceText.trim();
    const osType = document.getElementById('defineOsTypeSelect').value;
    const userStatus = document.getElementById('defineUserStatusSelect').value;

    if (checkNameExists(nameInput)) {
      setPreviewLoading(false);
      return;
    }

    try {
      const response = await dispatch(createAudienceDefinePreview({ name: nameInput, definition: definitionText , osType: osType, userStatus: userStatus })).unwrap();
      setPreviewData(response);
      setShowPreviewModal(true); 
    } catch (error) {
      showErrorAlert("Error", error);
    } finally {
      setPreviewLoading(false);
    }
  };



  const handleManualSubmit = async (formData) => {
    updateLoading(true);

    if (checkNameExists(formData.name)) return;

    try {
      await dispatch(createTargetManualy(formData)).unwrap();
      onSuccess && onSuccess();
    } catch (error) {
      showErrorAlert("Error", error);
      onError && onError(error);
    } finally {
      updateLoading(false);
    }

    toggle();
  };

  return (
    <>
    <Modal isOpen={isOpen} toggle={toggle} centered style={{ minWidth: '600px' }} className="modal-dialog modal-lg">
      <ModalHeader toggle={toggle}>Create Target Audience</ModalHeader>
      <ModalBody>
        <Nav tabs>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              onClick={() => toggleTab('1')}
            >
              Create Manually
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              onClick={() => toggleTab('2')}
            >
              Create with AI
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <CreateTargetManuallyForm
              createAudienceName={createAudienceName}
              setCreateAudienceName={setCreateAudienceName}
              profileTypeOptions={profileTypeOptions}
              selectedPersonaType={selectedPersonaType}
              setSelectedPersonaType={setSelectedPersonaType}
              endDate={endDate}
              startDate={startDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              updateLoading={updateLoading}
              setPopup={setPopup}
              onSubmit={handleManualSubmit}
              existingNames={existingNames}
              toggle={toggle}
            />
          </TabPane>
          <TabPane tabId="2">
            <CreateWithAIForm
              defineAudienceName={defineAudienceName}
              setDefineAudienceName={setDefineAudienceName}
              defineAudienceText={defineAudienceText}
              setDefineAudienceText={setDefineAudienceText}
              previewLoading={previewLoading}
              handleDefineSubmit={handleDefineSubmit}
            />
          </TabPane>
        </TabContent>
      </ModalBody>
      </Modal>

      {previewLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9998,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LottieAnimation />
        </div>
      )}

{activeTab === '2' && (
        <AudiencePreviewModal
          isOpen={showPreviewModal}
          toggle={() => setShowPreviewModal(false)}
          isLoading={isLoadingDefine}
          previewData={previewData}
          defineAudienceName={defineAudienceName}
          handleConfirm={handleConfirmDefine}
        />
      )}
    </>
  );
};

export default CreateTargetAudience;
